import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Spinner, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { saveGridHeader, saveGridContent } from 'redux/actions/authActions';
import { selectCurrentClient } from 'redux/actions/clientActions';
import { documentFailure, setCollapse } from 'redux/actions/documentActions';

import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as AngleArrowLeftIcon } from 'assets/icon-angle-left.svg';
import { ReactComponent as AngleArrowRightIcon } from 'assets/icon-angle-right.svg';

import { colors, fonts } from 'shared/defaultTheme';
import { Switch } from 'shared/components/form/Inputs';
import { emptyString } from 'shared/utils';
import { Checkbox } from 'shared/components/form/CheckBox';

import { SerachClientsForm, SerachClients, SearchDiv, Hr, Ul } from './styles';
import { CollapseClient, DocumentPanelBackground, CollapseClientRight } from '../styles';

const Clients = ({
  setSelectedClients,
  selectedClients,
  showMultipleSelect,
  setMultipleSelect,
}) => {
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const theme = useSelector((state) => state.theme);
  const auth = useSelector((state) => state.auth);
  const [searchValue, setSearchValue] = useState('');
  const { isCollapse } = useSelector((state) => state.document);
  const { currentClient } = useSelector((state) => state.clientState);
  const { t } = useTranslation();
  const { mainColor } = theme;
  const [clientId, setClientId] = useState('');
  const [clientList, setClientList] = useState([]);
  const [isCheckedAll, setCheckedAll] = useState(false);
  const [{ loading }, getClientsData] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${auth.cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  useEffect(() => {
    if (search && search.length) {
      const params = new URLSearchParams(search);
      const clientIdParam = params.get('clientId');
      setClientId(clientIdParam);
    }
  }, [search]);

  const clientsData = async () => {
    const {
      data: { data },
    } = await getClientsData({
      data: {
        filters: [
          {
            model: 'Client',
            field: 'ClientId',
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    });
    setClientList(data);
    if (!emptyString(clientId)) {
      const filterClient = data.find((x) => x.clientId === clientId);
      dispatch(selectCurrentClient(currentClient || filterClient));
    } else {
      dispatch(selectCurrentClient(currentClient || data[0]));
    }
    dispatch(documentFailure(null));
  };

  useEffect(() => {
    clientsData();
  }, [clientId]);

  const searchClients = async (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
      try {
        const {
          data: { data },
        } = await getClientsData({
          data: {
            filters: [
              {
                model: 'Client',
                field: 'ClientName',
                filter: [value],
                isRegex: true,
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
        setClientList(data);
      } catch (error) {
        setClientList([]);
      }
    } else {
      setSearchValue('');
      try {
        const {
          data: { data },
        } = await getClientsData({
          data: {
            filters: [
              {
                model: 'Client',
                field: 'ClientName',
                filter: [''],
                isRegex: true,
              },
            ],
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
        setClientList(data);
      } catch (error) {
        setClientList([]);
      }
    }
  };

  const onSelectClient = (client) => {
    const grid = document.querySelector('.e-sortfilter');
    const gridContent = document.querySelector('#file_grid_content_table');
    if (grid && gridContent) {
      dispatch(saveGridHeader(grid.outerHTML));
      dispatch(saveGridContent(gridContent.outerHTML));
    }
    dispatch(selectCurrentClient(client));
    dispatch(documentFailure(null));
  };

  const removeClient = (cli, index) => {
    const newClients = selectedClients.filter((item, i) => i !== index && item.clientId !== index);
    setSelectedClients(newClients);
  };

  const getSelectedClient = useCallback((e, item) => {
    const findClient = selectedClients.find((x) => x.clientId === item.clientId);
    if (!findClient) {
      const newClients = [
        ...selectedClients,
        {
          ...item,
        },
      ];
      onSelectClient(item);
      setSelectedClients(newClients);
    } else {
      removeClient(findClient, findClient.clientId);
    }
  });

  const onSelectActiveAll = (checked) => {
    if (checked) {
      setSelectedClients([]);
      setCheckedAll(false);
    } else {
      dispatch(selectCurrentClient(clientList[0]));
      setSelectedClients([]);
      setCheckedAll(false);
    }
  };

  const getSelectedColor = (client) => {
    if (currentClient?.clientId === client.clientId) {
      return true;
    }
    return false;
  };

  return (
    <>
      {!isCollapse ? (
        <DocumentPanelBackground className="mr-4">
          <SearchDiv className="d-flex mb-n2">
            <Col>
              <SerachClientsForm className="form">
                <SerachClients
                  Icon={() => null}
                  borderRadius={2}
                  className="mb-4 w-25 m-0 h-50"
                  inputClassName="w-100"
                  buttonClassName=""
                  withButtonClear
                  rightIcon={<SearchIcon fill={colors.black} />}
                  onChange={searchClients}
                  value={searchValue ?? ''}
                  placeholder={t('home_search_client')}
                />
              </SerachClientsForm>
            </Col>
            <CollapseClient onClick={() => dispatch(setCollapse(true))}>
              <AngleArrowLeftIcon />
            </CollapseClient>
          </SearchDiv>
          <div className="mb-n2 pl-3 d-flex align-items-center">
            <TextSpan>{t('documents_select_multiple')}</TextSpan>
            <Switch
              className="text-uppercase"
              id="switchActiveAll"
              onChange={(checked) => {
                setMultipleSelect(checked);
                onSelectActiveAll(checked);
              }}
              value={!showMultipleSelect}
              color={showMultipleSelect ? colors.bilbao : colors.error}
              classNameMain="ml-1"
            />
          </div>
          <Hr />
          <Ul className="list-unstyled">
            {!loading ? (
              <>
                {!showMultipleSelect ? (
                  <>
                    {clientList.map((client) => (
                      <Li
                        key={client.clientId}
                        onClick={() => onSelectClient(client)}
                        selectedColor={getSelectedColor(client)}
                        className="mb-2"
                      >
                        <div className="d-flex align-items-center">
                          <Div className="pl-4">{client.clientName}</Div>
                        </div>
                      </Li>
                    ))}
                  </>
                ) : (
                  <>
                    <MultipleSelectLi className="mb-2">
                      <div className="d-flex align-items-center">
                        <div className="checkbox-container">
                          <Checkbox
                            type="checkbox"
                            id="CheckedAll"
                            label=""
                            checked={isCheckedAll}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCheckedAll(!isCheckedAll);
                              if (!isCheckedAll) {
                                setSelectedClients([...clientList]);
                              } else {
                                setSelectedClients([]);
                              }
                            }}
                          />
                        </div>
                        <Div>
                          <label htmlFor="CheckedAll">{t('general_all')}</label>
                        </Div>
                      </div>
                    </MultipleSelectLi>
                    {clientList.map((client) => (
                      <MultipleSelectLi key={client.clientId} className="mb-2">
                        <div className="d-flex align-items-center">
                          <div className="checkbox-container">
                            <Checkbox
                              type="checkbox"
                              id={`filterCheckAll-${client.clientId}`}
                              label=""
                              checked={selectedClients.some(
                                (selectedClient) => selectedClient.clientId === client.clientId
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                getSelectedClient(e, client);
                              }}
                            />
                          </div>

                          <Div>
                            <label htmlFor={`filterCheckAll-${client.clientId}`}>
                              {client.clientName}
                            </label>
                          </Div>
                        </div>
                      </MultipleSelectLi>
                    ))}
                  </>
                )}
              </>
            ) : (
              <div className="d-flex justify-content-center mb-5 align-items-center h-100">
                <Spinner color={mainColor} />
              </div>
            )}
          </Ul>
        </DocumentPanelBackground>
      ) : (
        <div className="collapse-client-container mr-3">
          <CollapseClientRight onClick={() => dispatch(setCollapse(false))}>
            <AngleArrowRightIcon />
          </CollapseClientRight>
        </div>
      )}
    </>
  );
};

const Div = styled.div`
  width: 100%;
  height: 45px;
  padding-top: 15px;
  label {
    cursor: pointer;
  }
`;

const TextSpan = styled.div`
  color: ${colors.black};
  font-family: ${fonts.default};
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 16px;
`;

const Li = styled.li`
  background: ${({ selectedColor }) => (selectedColor ? colors.whiteSmoke : colors.white)};
  &:hover {
    background: ${colors.whiteSmoke};
  }
`;

const MultipleSelectLi = styled.li`
  background: ${colors.white};
  .checkbox-container {
    position: relative;
    left: 4px;
    padding-left: 10px;
    padding-top: 12px;
  }
`;

Clients.defaultProps = {
  setSelectedClients: null,
  selectedClients: null,
};

Clients.propTypes = {
  showMultipleSelect: PropTypes.bool.isRequired,
  setMultipleSelect: PropTypes.func.isRequired,
  setSelectedClients: PropTypes.func,
  selectedClients: PropTypes.arrayOf(PropTypes.shape({})),
};

export default Clients;
